import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { map } from 'rxjs/operators';
import { WaterBody } from '../models/water-body';
import { Advisory } from '../models/advisory';
import { AdvisoryService, AdvisoryApiType } from '../advisory.service';
import { ProfileService } from '../profile.service';

interface ArcGISExtentResponse {
  extent: {
    xmin: number; ymin: number; xmax: number; ymax: number;
    spatialReference: { wkid: number };
  };
}

const TAB_TO_API_TYPE: Record<'wq' | 'cso', AdvisoryApiType> = {
  wq: 'WQ',
  cso: 'CSO',
};

@Injectable({ providedIn: 'root' })
export class WaterbodiesService {
  private readonly http = inject(HttpClient);
  private readonly profile = inject(ProfileService);
  private readonly advisoryService = inject(AdvisoryService);

  getWaterBodies(tab: 'wq' | 'cso'): Observable<WaterBody[]> {
    const type = TAB_TO_API_TYPE[tab];
    const layerUrl = this.profile.get(`${tab}.arcgis.feature_layer.url`);
    return this.advisoryService.getAdvisories(type, new Date()).pipe(
      map(advisories => this.mapAdvisories(advisories, layerUrl))
    );
  }

  getExtent(wb: WaterBody): Observable<ArcGISExtentResponse> {
    if (!wb.sourceLayerUrl) return EMPTY;
    return this.http.get<ArcGISExtentResponse>(
      `${wb.sourceLayerUrl}/query?returnGeometry=true&returnExtentOnly=true&f=json&objectIds=${wb.id}`
    );
  }

  private mapAdvisories(advisories: Advisory[], sourceLayerUrl: string): WaterBody[] {
    return advisories.map(a => {
      const hasAdvisory = a.duration > 0;
      const wb: WaterBody = {
        id: a.waterbody.id,
        name: a.waterbody.name,
        class: a.waterbody.class,
        rainGauge: a.waterbody.rainGauge ?? undefined,
        hasAdvisory,
        sourceLayerUrl,
      };
      if (hasAdvisory) {
        const until = new Date(a.occurredOn);
        until.setHours(until.getHours() + a.duration);
        wb.advisory = { hours: a.duration, until, volume: a.volume };
      }
      return wb;
    });
  }
}
